'use client';

import { useEffect, useMemo, useState } from 'react';
import {
  splitOutput, splitOverview, splitNotes, splitStyles, splitVariants, isNoMistakes,
  VARIANT_ORDER, type Variant,
} from '@/lib/outputSections';
import { fetchLanguages, fetchModes, type Language, type Mode } from '@/lib/api';
import { modeHint, modeLabel, type InputLanguage } from '@/lib/modeText';
import CopyButton from './CopyButton';
import LanguageTabs from './LanguageTabs';
import ModeIcon from './ModeIcons';
import NoMistakes from './NoMistakes';

/**
 * A stored exchange, drawn the way the person who made it saw it.
 *
 * ── Why this is its own file ────────────────────────────────────────────────
 * It was written inside the admin console's `TranslationDialog`, and the
 * Notebook needs the same thing for a different reason: the console is an
 * operator looking at somebody else's row, the notebook is a person looking at
 * their own. Two copies of a replica is the one duplication this codebase
 * cannot afford — the whole claim a replica makes is "this is what the screen
 * looked like", and a second copy is a second thing to forget to update the
 * next time the pane changes. So there is one, and both callers frame it.
 *
 * Two rules hold it together, unchanged from where it came from:
 *
 *  1. **Nothing is generated here.** No model is called and no text is
 *     produced; the only requests are for the language and mode REGISTRIES, the
 *     same two public lists the app loads to draw its own chrome. The replica
 *     sits under `pointer-events-none`, so no click can re-translate anything.
 *
 *  2. **It reuses the real pieces.** `splitOutput`, `isNoMistakes`,
 *     `CopyButton`, `LanguageTabs`, `ModeIcon` and `NoMistakes` come from the
 *     app rather than being reimplemented, so a change to how the product
 *     renders an answer shows up here instead of silently drifting into a lie.
 *
 * ── Light island, or the app's own theme? ───────────────────────────────────
 * Both, and the caller decides. The console wraps this in `.gt-replay`, which
 * tailwind.config.js carves out of the dark variant — every `dark:` class below
 * stops applying inside it, and the operator sees the light skin the visitor
 * had. The notebook does not wrap it, so it follows the reader's own theme,
 * which is right for a person looking at their own work in their own app.
 * That is why the classes here carry `dark:` variants the console's original
 * did not: they cost the console nothing and they are what make the notebook
 * possible at all.
 */

/** Everything the replica needs. Deliberately not a `TranslationRow`. */
export interface ReplayRow {
  mode: string;
  sourceText: string;
  sourceLang: string;
  sourceLangName: string;
  sourceLangDetected: boolean;
  targetText: string;
  targetLang: string;
  targetLangName: string;
  ms: number;
  cached: boolean;
  /**
   * The tabbed modes, when the tabs arrived as SEPARATE answers.
   *
   * Registers and AI Prompt have been one request per tab since 2026-08, so a
   * notebook entry carries a map of them. A console row does not: it is one
   * request, and its tabs (if it has any) are tags inside `targetText`. Both
   * are handled below — the map wins when it has more than one key, and the
   * tag-splitting is the fallback, which is also what every row written before
   * the split still needs.
   */
  tabs?: Record<string, string> | null;
  activeTab?: string | null;
}

/** The three registers, named as the pane names them. */
const VARIANT_LABEL: Record<Variant['key'], string> = {
  formal: 'Formal',
  friendly: 'Friendly',
  casual: 'Slang',
};

/** `claude-session` → `Claude session`, for a prompt style the registry has lost. */
/**
 * The Translate pane's three tabs, by the key the notebook stored them under.
 *
 * `original` is the faithful tab from before it was renamed Standard (same
 * prompt, so the same name now). `easy` is the Easy tab (A1, then translated);
 * older `easy` rows were written by the clearer-rewording prompt of the same
 * name, which is still a fair name for what is in them.
 */
const TRANSLATE_TAB_LABEL: Record<string, string> = { standard: 'Standard', original: 'Standard', ai: 'Software-AI', easy: 'Easy' };

function titleCase(id: string): string {
  const words = id.replace(/[-_]+/g, ' ').trim();
  return words ? words[0].toUpperCase() + words.slice(1) : id;
}

export default function TranslationReplica({
  row,
  modes: modesProp,
  languages: languagesProp,
  styleLabels: styleLabelsProp,
  note,
  footer,
  maxWidth = 'max-w-4xl',
}: {
  row: ReplayRow;
  /**
   * The registries, when the caller already has them.
   *
   * The translator holds both in state, so the notebook hands them over rather
   * than making this component fetch the same two lists again every time a
   * different entry is opened. The console does not, and passes nothing — the
   * effect below covers it.
   */
  modes?: Mode[] | null;
  languages?: Language[] | null;
  styleLabels?: Record<string, string>;
  /** The line under the language bar. The console explains its reconstruction. */
  note?: React.ReactNode;
  /** Anything the frame wants under the output pane — buttons, a note field. */
  footer?: React.ReactNode;
  /**
   * How wide the replica is allowed to get.
   *
   * `max-w-4xl` by default, which is the translator's own reading measure and
   * the right cap when this is one card on a page. The Notebook passes
   * `max-w-none`: there the replica has a column to itself in a dialog that is
   * 95% of the screen, and capping it drew the panes down the middle of a great
   * deal of empty space.
   */
  maxWidth?: string;
}) {
  const [modesState, setModes] = useState<Mode[] | null>(null);
  const [labelsState, setStyleLabels] = useState<Record<string, string>>({});
  const [languagesState, setLanguages] = useState<Language[] | null>(null);

  const needsRegistries = !modesProp || !languagesProp;

  useEffect(() => {
    if (!needsRegistries) return;
    let alive = true;
    void fetchModes()
      .then((m) => {
        if (!alive) return;
        setModes(m.modes);
        // AI Prompt's tabs are operator-editable, so their names live in the
        // registry rather than in the answer — which carries only `[[STYLE:id]]`.
        setStyleLabels(Object.fromEntries((m.promptStyles ?? []).map((p) => [p.styleId, p.label])));
      })
      .catch(() => {});
    void fetchLanguages()
      .then((l) => { if (alive) setLanguages(l); })
      .catch(() => {});
    return () => { alive = false; };
  }, [needsRegistries]);

  const modes = modesProp ?? modesState;
  const languages = languagesProp ?? languagesState;
  const styleLabels = styleLabelsProp ?? labelsState;

  /** The row's mode as the registry knows it — absent once it has been retired. */
  const usedMode = modes?.find((m) => m.id === row.mode) ?? null;

  const inputLang: InputLanguage = {
    code: row.sourceLang,
    name: row.sourceLangName,
    country: languages?.find((l) => l.code === row.sourceLang)?.countries?.[0] ?? null,
  };

  /*
   * The overview rides at the end of the stored answer, tag and all. Take it
   * off FIRST, or the tag is drawn as the last line of the translation, which
   * is the one thing the user never saw.
   */
  const { body: afterOverview, overview } = splitOverview(row.targetText);
  const { body, notes: taught } = splitNotes(afterOverview);
  const { main, notes: modeNotes } = splitOutput(body);
  const clean = isNoMistakes(body);

  /*
   * ── THE ANSWER'S OWN TABS ────────────────────────────────────────────────
   *
   * Two sources, in this order:
   *
   *   1. `row.tabs` — the notebook's map, one answer per key, because each was
   *      its own request. Each value is a whole answer and still carries its
   *      own `[[FORMAL]]` / `[[STYLE:id]]` tag and possibly its own overview,
   *      so each is put through the same splitters as the main body.
   *   2. the tags inside `targetText`, for a console row and for anything
   *      written before the per-tab split.
   *
   * Registers are drawn from `VARIANT_ORDER` rather than from the answer, for
   * the reason the pane does it: the three are fixed, so a register the model
   * never wrote is an EMPTY tab that says so, not a tab that is silently
   * missing.
   */
  const answerTabs = useMemo((): { key: string; label: string; text: string }[] | null => {
    if (clean) return null;

    const fromMap = row.tabs && Object.keys(row.tabs).length > 1 ? row.tabs : null;
    if (fromMap) {
      const bodyOf = (raw: string) => {
        const { body: b } = splitOverview(raw);
        const { main: m } = splitOutput(splitNotes(b).body);
        // One answer, one tag — `splitVariants`/`splitStyles` return the single
        // section, and its `.text` is the tag-free answer.
        return splitVariants(m)?.[0]?.text ?? splitStyles(m)?.[0]?.text ?? m;
      };
      const keys = Object.keys(fromMap);
      const isRegister = keys.every((k) => (VARIANT_ORDER as string[]).includes(k));
      if (isRegister) {
        return VARIANT_ORDER.map((key) => ({
          key,
          label: VARIANT_LABEL[key],
          text: fromMap[key] ? bodyOf(fromMap[key]) : '',
        }));
      }
      return keys.map((key) => ({
        key,
        label: styleLabels[key] ?? TRANSLATE_TAB_LABEL[key] ?? titleCase(key),
        text: bodyOf(fromMap[key]),
      }));
    }

    const variants = splitVariants(main);
    if (variants) {
      return VARIANT_ORDER.map((key) => ({
        key,
        label: VARIANT_LABEL[key],
        text: variants.find((v) => v.key === key)?.text ?? '',
      }));
    }
    const styles = splitStyles(main);
    if (styles) {
      return styles.map((st) => ({
        key: st.styleId,
        // A style retired since is not in the registry any more; its id, tidied,
        // is a better tab name than nothing.
        label: styleLabels[st.styleId] ?? titleCase(st.styleId),
        text: st.text,
      }));
    }
    return null;
  }, [clean, row.tabs, main, styleLabels]);

  const [tab, setTab] = useState<string | null>(null);
  // A new entry is a new answer with its own tabs. Without this, opening a
  // Registers row on Slang and then a five-style AI Prompt row leaves `tab`
  // holding a key the second answer has never heard of.
  useEffect(() => setTab(null), [row.sourceText, row.mode, row.targetText]);
  const openTab = tab ?? row.activeTab ?? answerTabs?.[0]?.key ?? null;
  const openText = answerTabs?.find((t) => t.key === openTab)?.text ?? '';

  // Both kinds of card, in the app's order: whatever the mode wrote first, then
  // the learner notes the Overview switch adds.
  const noteCards = [modeNotes, taught]
    .filter(Boolean)
    .map((section) => {
      const [head, ...rest] = section.split('\n');
      return { section, head: head.trim(), body: rest.join('\n').trim() };
    })
    .filter((c) => !!c.body);
  const hasNotes = noteCards.length > 0;

  return (
    /*
     * `pointer-events-none` on the whole replica: this is the rule that lets the
     * app's own interactive components be reused without any of them being live.
     * Every handler passed in is already a no-op, but a no-op still takes focus,
     * still opens a ▾ panel, still looks like it should have done something.
     * The panes and the tab row are put back on — reading and copying are what
     * somebody actually does in here, and neither changes anything.
     */
    <div className={'pointer-events-none mx-auto flex w-full flex-col gap-3 ' + maxWidth}>
      {/*
        THE APP'S OWN LANGUAGE BAR, not a summary of it. A mode on its own says
        nothing; a mode lit among the six says which of the six, and a bar with
        the target half missing says the mode had no second language to offer.
      */}
      <LanguageTabs
        languages={languages ?? []}
        from={row.sourceLangDetected ? 'auto' : row.sourceLang}
        to={row.targetLang}
        sourceTabs={replayTabs(row.sourceLang, languages)}
        targetTabs={replayTabs(row.targetLang, languages)}
        detected={row.sourceLangDetected ? { code: row.sourceLang, name: row.sourceLangName } : null}
        lockedTarget={usedMode?.forcesTarget ?? null}
        followsInput={!!usedMode?.followsInputLanguage}
        inputLangName={row.sourceLangName}
        swapBlocked="This is a stored translation, not a translator."
        replay
        onFrom={() => {}}
        onTo={() => {}}
        onSwap={() => {}}
        // Not recorded as a flag — but an answer carrying an overview tag is an
        // answer the switch was ON for, which is the same fact read off the only
        // place it survives.
        overview={!!overview}
        overviewAttention={false}
        onOverview={() => {}}
      />

      {note && <div className="-mt-1 px-1 text-[11px] text-ink-400">{note}</div>}

      {/* The two panes with the mode column between them. */}
      <div
        className="grid grid-cols-1 rounded-2xl border border-ink-200 bg-white shadow-sm
                   md:grid-cols-[1fr_64px_1fr] dark:border-ink-700 dark:bg-ink-950"
      >
        <div
          className="pointer-events-auto min-h-[220px] rounded-t-2xl border-b border-ink-200 p-5
                     md:rounded-l-2xl md:rounded-tr-none md:border-b-0 md:p-7 dark:border-ink-700"
        >
          {/* The input's Copy where the app has it: an icon at the top right of the box
              (the app puts Clear beside it; a replica has nothing to clear). */}
          <div className="relative">
            <span className="absolute right-0 top-0">
              <CopyButton iconOnly text={row.sourceText} label="Copy text" />
            </span>
            <p className="whitespace-pre-wrap break-words pr-9 text-[19px] leading-relaxed text-ink-900 dark:text-ink-50">
              {row.sourceText}
            </p>
          </div>
          <div className="flex items-center justify-end pt-3 text-xs text-ink-400">
            <span>{row.sourceText.length.toLocaleString()} characters</span>
          </div>
        </div>

        <ModeRail modes={modes} used={row.mode} lang={inputLang} />

        <div
          className="pointer-events-auto min-h-[220px] rounded-b-2xl bg-ink-50/40 p-5
                     md:rounded-r-2xl md:rounded-bl-none md:p-7 dark:bg-ink-800"
        >
          {/* The overview, above the answer — the app's position, because this
              pane is a replica and not a second design. */}
          {overview && (
            <div className="mb-4 flex gap-2.5 rounded-xl border border-accent-500/25 bg-accent-50/70
                            px-3.5 py-3 dark:bg-accent-500/10">
              <svg viewBox="0 0 24 24" className="mt-[3px] h-4 w-4 shrink-0 text-accent-600 dark:text-accent-400" fill="currentColor" aria-hidden>
                <path d="M13 2L4.5 13.5H11l-1 8.5 8.5-11.5H12z" />
              </svg>
              <p className="whitespace-pre-wrap break-words text-[15px] leading-relaxed text-ink-700 dark:text-ink-200">
                {overview}
              </p>
            </div>
          )}
          <div>
            {clean ? (
              <NoMistakes />
            ) : answerTabs ? (
              <div>
                {/* The app's own tab row, class for class. `pointer-events-auto`
                    because this is reading, not acting: switching a tab reveals
                    a slice of text that is already here. */}
                <div
                  className="pointer-events-auto mb-3 flex flex-wrap items-center gap-1 border-b
                             border-ink-200 dark:border-ink-700"
                  role="tablist"
                >
                  {answerTabs.map((t) => {
                    const on = t.key === openTab;
                    return (
                      <button
                        key={t.key}
                        type="button"
                        role="tab"
                        aria-selected={on}
                        onClick={() => setTab(t.key)}
                        title={t.text ? undefined : 'This answer has no such version'}
                        className={
                          'relative -mb-px flex items-center gap-1.5 rounded-t-lg px-3 py-1.5 ' +
                          'text-[13px] font-medium transition ' +
                          (on
                            ? 'border-b-2 border-accent-600 text-accent-700 dark:border-accent-400 dark:text-accent-300'
                            : 'border-b-2 border-transparent text-ink-500 hover:text-ink-800 dark:text-ink-400 dark:hover:text-ink-100') +
                          (t.text ? '' : ' opacity-40')
                        }
                      >
                        {t.label}
                      </button>
                    );
                  })}
                </div>
                {openText ? (
                  <p className="whitespace-pre-wrap break-words text-[17px] leading-relaxed text-ink-900 dark:text-ink-50">
                    {openText}
                  </p>
                ) : (
                  // An empty register is a fact about the answer, not a
                  // rendering failure, and saying which is the difference
                  // between "the model skipped it" and "this is broken".
                  <p className="text-[15px] leading-relaxed text-ink-400">
                    The model did not write this version in this answer.
                  </p>
                )}
              </div>
            ) : (
              <p className="whitespace-pre-wrap break-words text-[19px] leading-relaxed text-ink-900 dark:text-ink-50">
                {main}
              </p>
            )}
          </div>

          <div className={'flex items-center justify-between ' + (hasNotes ? 'mt-4' : 'pt-3')}>
            <span className="text-xs text-ink-400">
              {row.cached ? 'instant · from cache' : row.ms ? `${(row.ms / 1000).toFixed(1)}s` : ''}
            </span>
            {/* The app's one Copy: the open version's text only, as on the
                page (no copy in the tab row or on the cards — owner, 2026-10-14). */}
            <CopyButton text={answerTabs ? openText : main} disabled={clean || (!!answerTabs && !openText)} />
          </div>

          {noteCards.map((card) => (
            <div
              key={card.head}
              className="mt-4 rounded-xl border border-ink-200 bg-white/70 p-4
                         dark:border-ink-700 dark:bg-ink-900/60"
            >
              <div className="mb-2">
                <span className="text-sm font-semibold text-ink-700 dark:text-ink-100">{card.head}</span>
              </div>
              <p className="whitespace-pre-wrap break-words text-[15px] leading-relaxed text-ink-700 dark:text-ink-200">
                {card.body}
              </p>
            </div>
          ))}
        </div>
      </div>

      {footer && <div className="pointer-events-auto">{footer}</div>}
    </div>
  );
}

/**
 * THE WHOLE RAIL, with the entry's mode lit.
 *
 * A deliberate replica rather than `ModeColumn` itself, and for a reason about
 * behaviour rather than looks: `ModeColumn` runs the new-mode attract animation
 * and writes `saveModeTried` to localStorage the moment its active mode is the
 * spotlit one. Pointed at a stored row that would wave a hint at somebody who
 * is not choosing anything, and quietly mark a "they have tried it" flag on
 * their behalf. A replay must not leave fingerprints. Everything the eye reads
 * — the 64px column, the 40px buttons, the selected fill, the desaturated rest
 * state, the icons — comes from the same classes and the same `ModeIcon`.
 */
function ModeRail({
  modes,
  used,
  lang,
}: {
  modes: Mode[] | null;
  used: string;
  lang: InputLanguage;
}) {
  const list = modes ?? [];
  // A mode retired since is in no registry and would vanish from its own
  // record. It is drawn from the row instead, at the end, and said out loud.
  const retired = list.length > 0 && !list.some((m) => m.id === used);

  return (
    <div
      className="flex shrink-0 flex-row justify-center gap-1.5 border-ink-200 px-2 py-3
                 md:w-16 md:flex-col md:justify-start md:border-x dark:border-ink-700"
    >
      <span className="hidden pb-1 text-center text-[10px] font-semibold uppercase tracking-wider text-ink-400 md:block">
        Mode
      </span>

      {list.map((m) => {
        const on = m.id === used;
        const label = modeLabel(m.label, lang.name);
        return (
          <div
            key={m.id}
            // Native `title`, not the app's hover card — the hover card needs a
            // pointer and the replica has none.
            title={`${label}\n${modeHint(m.hint, lang.name)}${on ? '\n\n← this one' : ''}`}
            aria-label={`${label}${on ? ' — the mode this translation used' : ''}`}
            aria-current={on ? 'true' : undefined}
            className={
              'flex h-10 w-full items-center justify-center rounded-lg border ' +
              (on
                ? 'border-accent-500 bg-accent-600 text-white shadow-sm'
                : 'border-transparent text-ink-900/70 dark:text-ink-dim')
            }
          >
            <span
              className={
                'flex items-center justify-center ' +
                // The column's own rule: colour is earned. The gem keeps its
                // fire at rest; the chosen mode gets its colour back.
                (on || m.icon === 'gem' ? 'grayscale-0' : 'grayscale')
              }
            >
              <ModeIcon name={m.icon ?? null} label={label} lang={lang} />
            </span>
          </div>
        );
      })}

      {(retired || list.length === 0) && (
        <div
          title={retired ? `${used} — this mode has since been retired; the entry is kept.` : used}
          aria-label={`${used} — the mode this translation used`}
          aria-current="true"
          className="flex h-10 w-full items-center justify-center rounded-lg border
                     border-accent-500 bg-accent-600 text-white shadow-sm"
        >
          <ModeIcon name={iconForMode(used)} label={used} lang={lang} />
        </div>
      )}

      {retired && (
        <span className="hidden text-center text-[9px] leading-tight text-ink-400 md:block">
          retired
        </span>
      )}
    </div>
  );
}

/**
 * The three languages one half of the bar shows, with this row's first.
 *
 * A RECONSTRUCTION, and the only one in the replica — which three tabs somebody
 * had is per-browser and most-recently-used (lib/languagePrefs) and was never
 * stored. So the bar is filled the way that file seeds an empty browser, with
 * the row's own language promoted to the front. The two lit tabs are the only
 * claim being made.
 */
const REPLAY_FILL = ['en', 'zh', 'es', 'ja', 'ko'];
const REPLAY_TABS = 3;

export function replayTabs(code: string, languages: Language[] | null): string[] {
  const known = languages ? new Set(languages.map((l) => l.code)) : null;
  const out = [code];
  for (const c of REPLAY_FILL) {
    if (out.length >= REPLAY_TABS) break;
    if (c === code) continue;
    // Skipped rather than guessed at when the registry has not landed: an
    // unknown code renders as the code itself, and `es` sitting in the bar
    // where "Spanish" belongs looks like a bug.
    if (known && !known.has(c)) continue;
    if (known) out.push(c);
  }
  return out;
}

/**
 * The icon a mode uses, for a mode the registry no longer has.
 *
 * A local map rather than a fetch: a mode retired since (the entries for it
 * stay forever) would come back with nothing. An unknown id falls through to
 * `ModeIcon`'s initials, which is what the real column does.
 */
export function iconForMode(mode: string): string | null {
  const MAP: Record<string, string> = {
    translate: 'arrow',
    natural: 'sparkle',
    formal: 'tie',
    casual: 'smile',
    'simple-a1': 'graduation',
    'simple-a2': 'graduation',
    'simple-b1': 'graduation',
    // Rows logged before 2026-08-07, when this became `native`.
    'us-english': 'flag-us',
    native: 'flag-us',
    humanize: 'human',
    'ai-prompt': 'gem',
    mistakes: 'proofread',
    explain: 'lightbulb',
  };
  return MAP[mode] ?? null;
}
